// Widget reads only need PostgREST. Importing supabase-js also initializes auth
// storage probes before consent, even when persistSession is false.
import { PostgrestClient } from '@supabase/postgrest-js';
import { FRONTEND_VERSION } from '../config/constants';
import type { Database } from '../types/database.generated';
import { logger } from '../utils/core/logger';
import { RequestCancelledError } from '../utils/core/retryUtils';

// Supabase client singleton - lazily initialized on first access
let supabaseClient: PostgrestClient<Database> | null = null;

// ============================================================================
// TIMEOUT CONFIGURATION
// ============================================================================

// Timeout for all Supabase requests (10 seconds)
const SUPABASE_TIMEOUT_MS = 10000;

type TimedError = Error & {
    code?: string;
    duration?: number;
    timeout?: number;
};

function isAbortError(error: unknown): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        'name' in error &&
        error.name === 'AbortError'
    );
}

function addDurationToError(error: unknown, duration: number): void {
    if (typeof error === 'object' && error !== null) {
        (error as { duration?: number }).duration = duration;
    }
}

/**
 * Custom fetch implementation with timeout support
 *
 * Wraps the native fetch API to add configurable timeout using AbortController.
 * Tracks request duration and provides enhanced error messages for timeouts.
 *
 * @param timeoutMs - Timeout in milliseconds (default: 10000ms)
 * @returns Custom fetch function compatible with Supabase client
 */
const createFetchWithTimeout = (timeoutMs: number) => {
    return async (url: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const startTime = Date.now();

        try {
            const response = await fetch(url, {
                ...init,
                credentials: 'omit',
                referrerPolicy: 'no-referrer',
                signal: controller.signal,
            });
            clearTimeout(timeoutId);

            const duration = Date.now() - startTime;
            logger.debug('Supabase request completed', {
                url: typeof url === 'string' ? url : url.toString(),
                status: response.status,
                duration_ms: duration,
            });

            return response;
        } catch (error) {
            clearTimeout(timeoutId);
            const duration = Date.now() - startTime;

            if (isAbortError(error)) {
                // Use 90% threshold to account for timer drift
                if (duration >= timeoutMs * 0.9) {
                    // Real timeout — the request exceeded the time limit
                    const timeoutError: TimedError = new Error(
                        `Supabase request timeout after ${timeoutMs}ms (actual: ${duration}ms)`
                    );
                    timeoutError.code = 'TIMEOUT';
                    timeoutError.duration = duration;
                    timeoutError.timeout = timeoutMs;
                    throw timeoutError;
                }
                // Navigation or component unmount aborted the request — not a real error
                throw new RequestCancelledError(duration);
            }

            // Preserve original error but add timing information
            addDurationToError(error, duration);
            throw error;
        }
    };
};

/**
 * Get the Supabase client, initializing it on first access.
 *
 * @throws Error if required environment variables are missing
 */
export function getSupabase(): PostgrestClient<Database> {
    if (supabaseClient) {
        return supabaseClient;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseAnonKey) {
        const error = new Error('Missing required Supabase configuration');
        logger.error('Failed to initialize Supabase:', {
            hasUrl: !!supabaseUrl,
            hasKey: !!supabaseAnonKey,
        });
        throw error;
    }

    supabaseClient = new PostgrestClient<Database>(`${supabaseUrl.replace(/\/$/, '')}/rest/v1`, {
        headers: {
            apikey: supabaseAnonKey,
            Authorization: `Bearer ${supabaseAnonKey}`,
            'X-Client-Info': 'inboundx-widget',
            'X-Client-Version': FRONTEND_VERSION,
        },
        fetch: createFetchWithTimeout(SUPABASE_TIMEOUT_MS),
    });

    logger.debug('Supabase client initialized');
    return supabaseClient;
}
